import { z } from "zod";

import { searchFilterSourceSchema } from "../operator/search-filter.dto";

/**
 * The storefront filter bar's *definition* — what `GET /api/v1/search-filters`
 * returns. The operator side of this contract lives in
 * `../operator/search-filter.dto.ts`; this is the read model travelers get.
 *
 * Two things differ from the operator shape:
 *
 *  - **Slugs, not ids.** The traveler URL carries `?facet=<typeSlug>:<valueSlug>`,
 *    so slugs are the identifiers on this surface. Ids are an operator concern.
 *  - **Counts, not usage.** The operator reads `usedOnTours` to warn before a
 *    delete; a traveler reads how many published tours an option would leave.
 */
export const searchFilterBarOptionSchema = z.object({
  slug: z.string().min(1),
  label: z.string().min(1),
  /** Published tours carrying this option. Zero is a real answer, not a gap —
   *  an option nobody has assigned yet renders as `(0)` rather than vanishing,
   *  which is what tells the operator their new filter needs assigning. */
  count: z.number().int().nonnegative(),
});

export const searchFilterBarItemSchema = z.object({
  slug: z.string().min(1),
  source: searchFilterSourceSchema,
  /** Already resolved server-side — the operator's override when they set one,
   *  otherwise the underlying facet type's own name. */
  label: z.string().min(1),
  /**
   * True when `label` is the operator's OWN wording (a `filter_label` override),
   * false when it is the seeded name the platform shipped.
   *
   * The storefront needs the distinction because a `builtin` / `taxonomy`
   * heading it has not been renamed is one of ITS strings, which it translates
   * and words its own way — `t("Destination")`, not the seeded "Destinations".
   * Rendering the resolved label unconditionally would swap that heading on
   * every agency that never renamed anything, and drop the translation with it.
   * `custom` filters are operator-authored either way, so this never applies to
   * them.
   */
  renamed: z.boolean(),
  /**
   * Populated for `custom` filters only. `builtin` (duration / price / date)
   * compute their own controls on the storefront, and `taxonomy`
   * (destination / activity) already arrive through
   * `/api/v1/destinations` and `/api/v1/experience-types` with their own
   * counts — duplicating either here would give the bar two sources of truth.
   */
  options: z.array(searchFilterBarOptionSchema),
});

/**
 * Three distinct answers, and the storefront draws a different bar for each:
 *
 *  - `enabled: false` — the agency-wide switch is off: no bar at all (AC-08).
 *  - `arranged: false` — nobody has arranged anything, which is what an agency
 *    without the Search Filters app gets: draw the platform's default bar
 *    (AC-01).
 *  - `arranged: true` — `filters` IS the operator's arrangement. Render exactly
 *    it, including when it is empty.
 *
 * `arranged` exists because the last two cases both carry `filters: []` and
 * mean opposite things. An installed agency that hid every filter has arranged
 * something — an empty bar — and a client that reads emptiness alone as "no
 * configuration" resurrects the full default bar, the precise opposite of what
 * the operator asked for.
 */
export const searchFilterBarSchema = z.object({
  enabled: z.boolean(),
  arranged: z.boolean(),
  filters: z.array(searchFilterBarItemSchema),
});

export type SearchFilterBarOptionDto = z.infer<
  typeof searchFilterBarOptionSchema
>;
export type SearchFilterBarItemDto = z.infer<typeof searchFilterBarItemSchema>;
export type SearchFilterBarDto = z.infer<typeof searchFilterBarSchema>;

/**
 * Separator in the traveler URL's `?facet=<typeSlug>:<valueSlug>` token.
 *
 * Slugs are `[a-z0-9-]`, so a colon can never occur inside either half — which
 * is why one character is enough and why a token carrying two of them is
 * malformed rather than nested.
 */
export const FACET_TOKEN_SEPARATOR = ":";

/**
 * The shape traveler-api's query schema enforces on each half
 * (`list-public-experiences.query.ts` — `/^[a-z0-9-]+:[a-z0-9-]+$/`). Anything
 * outside it is a 400 for the WHOLE request, not a dropped filter, so the
 * storefront must reject the same set before it builds a query — otherwise one
 * hand-edited character in the address bar takes the entire tours page down.
 */
const FACET_SLUG_REGEX = /^[a-z0-9-]+$/;

export function formatFacetToken(typeSlug: string, valueSlug: string): string {
  return `${typeSlug}${FACET_TOKEN_SEPARATOR}${valueSlug}`;
}

/**
 * `null` for anything that is not exactly two slug-shaped halves. Deliberately
 * stricter than a bare `split(":")` destructure, which reads `a:b:c` as `a`/`b`
 * and silently drops the rest — a malformed token should drop out of the filter
 * set, not quietly filter on half of itself.
 *
 * Both traveler-web (which writes tokens into the URL) and traveler-api (which
 * reads them off the query) go through this, so the two cannot disagree about
 * what a token is.
 */
export function parseFacetToken(
  token: string,
): { typeSlug: string; valueSlug: string } | null {
  const parts = token.split(FACET_TOKEN_SEPARATOR);
  if (parts.length !== 2) return null;
  const [typeSlug, valueSlug] = parts;
  if (!typeSlug || !valueSlug) return null;
  if (!FACET_SLUG_REGEX.test(typeSlug)) return null;
  if (!FACET_SLUG_REGEX.test(valueSlug)) return null;
  return { typeSlug, valueSlug };
}
